import type { FareView, WaitingView } from '@limuzin/passenger';

/**
 * The meter, between server responses.
 *
 * A port of `Fare` in `apps/rider/lib/models/ride.dart`, field for field and rule for
 * rule. The server's figure replaces all of this whenever a response arrives — this
 * is for the seconds in between, not a second opinion about the fare.
 *
 * The split the server hands over is what makes it safe: the pickup wait is settled,
 * ended stops are settled, and only `waitingSince` is still running. Both apps once
 * added a live clock to an aggregate that already contained it and charged the pickup
 * wait twice while under way.
 */

const secondsSince = (iso: string, now: number): number =>
  Math.max(0, Math.floor((now - Date.parse(iso)) / 1000));

/**
 * A fifth of the rate for every 12 seconds, the part-minute in whole rubles, and a
 * whole minute exactly the rate — `chargedKopeks` in `fare.ts` on the server.
 */
function chargedKopeks(seconds: number, perMinuteKopeks: number): number {
  if (seconds <= 0) return 0;
  const steps = Math.floor((seconds % 60) / 12);
  return (
    Math.floor(seconds / 60) * perMinuteKopeks +
    Math.floor((steps * perMinuteKopeks + 250) / 500) * 100
  );
}

/** Seconds of the stop that is running right now, if one is. */
export function liveWaitSeconds(fare: FareView, now: number): number {
  return fare.waitingSince ? secondsSince(fare.waitingSince, now) : 0;
}

/**
 * The whole ride since boarding, stops and all — the «Время в пути» row, which a stop
 * does not pause, as it does not pause the driver's «Время». `liveRideSeconds` in
 * `apps/rider/lib/models/ride.dart` is the same reading, and the row is written from
 * it in both clients.
 */
export function liveRideSeconds(fare: FareView, now: number): number {
  return fare.startedAt ? secondsSince(fare.startedAt, now) : 0;
}

/**
 * Time in transit, which the fare bills by the fifth of a minute. Mid-ride stops come out; the
 * pickup wait does not, because it happened before `startedAt` and was never in here.
 * This is a billing input and not the row: what the passenger reads as «Время в пути»
 * is [liveRideSeconds], which a stop does not pause.
 */
export function liveTransitSeconds(fare: FareView, now: number): number {
  if (!fare.startedAt) return 0;
  const gross = secondsSince(fare.startedAt, now);
  return Math.max(0, gross - fare.inRideSettledSeconds - liveWaitSeconds(fare, now));
}

export function liveWaitingSeconds(fare: FareView, now: number): number {
  return fare.pickupPaidSeconds + fare.inRideSettledSeconds + liveWaitSeconds(fare, now);
}

/** The «Стоимость» row, recomputed each second so it moves the way a meter does. */
export function liveTotalKopeks(fare: FareView, now: number): number {
  // A fixed price does not tick. The passenger is never shown a figure that is not
  // the figure (ADR 0003).
  if (fare.setByDriver || !fare.startedAt) return fare.totalKopeks;
  return (
    fare.tariff.boardingKopeks +
    // Zero unless this ride is a предзаказ: the server sends what this ride adds.
    fare.tariff.preorderKopeks +
    // «Бесплатно в пути» comes off the seconds, as `fare.ts` on the server takes it.
    chargedKopeks(
      liveTransitSeconds(fare, now) - fare.tariff.freeTransitSeconds,
      fare.tariff.perMinuteKopeks,
    ) +
    chargedKopeks(liveWaitingSeconds(fare, now), fare.tariff.waitingPerMinuteKopeks)
  );
}

/** How the chip at the pickup reads at one instant. */
export interface WaitingChip {
  paid: boolean;
  /** «Бесплатное ожидание», and then «Платное ожидание». */
  label: string;
  seconds: number;
}

/**
 * The waiting chip as it reads at [now].
 *
 * Counting *down* while the free allowance lasts and *up* once it is spent is the
 * whole behaviour of it, and which of the two it is gets decided here rather than
 * on the server: nothing happens in the service when an allowance runs out — no
 * milestone, no status — so a chip that had been sent the word held «Бесплатное
 * ожидание 0:00» until some unrelated event refetched the ride.
 *
 * The words and the second they turn over on are the driver app's own, so the two
 * screens at the kerb never disagree about whether waiting is still free.
 */
export function waitingChip(waiting: WaitingView, now: number): WaitingChip {
  const waited = secondsSince(waiting.startedAt, now);
  return waited < waiting.freeSeconds
    ? { paid: false, label: 'Бесплатное ожидание', seconds: waiting.freeSeconds - waited }
    : { paid: true, label: 'Платное ожидание', seconds: waited - waiting.freeSeconds };
}

/** «12:07». A meter reads in minutes and seconds, not in «7 минут». */
export function clock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}
